import Chart from "chart.js/auto";
import { CONTENT_WIDTH, bottomMarginItemGlobal } from "../../utils/pdfConstants";

// Colors match PiechartPTS/PTSLongTermTable UI: Rakennetekniikka, LVI, Sähkö, Lisätutkimukset
const PALETTE = ["#7AA668", "#A7BFA2", "#C8D1BC", "#2F5930"];

const COLORS = {
  green:      "#04aa00",
  headerText: "white",
};

const sum = (arr = []) => arr.reduce((a, b) => a + (parseFloat(b) || 0), 0);

async function canvasToBase64(canvas) {
  return canvas.toDataURL("image/png");
}

const stackTotalLabels = {
  id: "stackTotalLabels",
  afterDatasetsDraw(chart) {
    const { ctx, data, scales: { x, y } } = chart;
    const n = data.labels.length;
    for (let i = 0; i < n; i++) {
      const total = data.datasets.reduce((s, ds) => s + (ds.data[i] || 0), 0);
      if (!total) continue;
      const xPx = x.getPixelForValue(i);
      const yPx = y.getPixelForValue(total);
      ctx.save();
      ctx.fillStyle = "#333333";
      ctx.font = "bold 13px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      ctx.fillText(String(Math.round(total)), xPx, yPx - 4);
      ctx.restore();
    }
  },
};

async function renderBarChart(chartData, years) {
  const canvas = document.createElement("canvas");
  canvas.width = 900;
  canvas.height = 420;
  const ctx = canvas.getContext("2d");

  const keys = ["Lisätutkimukset", "Sähköjärjestelmät", "LVI Järjestelmät", "Rakennetekniikka"];
  const maxTotal = Math.max(...chartData.map(d => keys.reduce((s, k) => s + (d[k] || 0), 0)), 0);
  const yMax = Math.ceil(maxTotal * 1.15);

  const chart = new Chart(ctx, {
    type: "bar",
    data: {
      labels: years,
      datasets: [
        {
          label: "Lisätutkimukset",
          data: chartData.map(d => d["Lisätutkimukset"] || 0),
          backgroundColor: PALETTE[3],
          borderRadius: 6,
          borderSkipped: false,
          stack: "a",
        },
        {
          label: "Sähköjärjestelmät",
          data: chartData.map(d => d["Sähköjärjestelmät"] || 0),
          backgroundColor: PALETTE[2],
          borderRadius: 6,
          borderSkipped: false,
          stack: "a",
        },
        {
          label: "LVI Järjestelmät",
          data: chartData.map(d => d["LVI Järjestelmät"] || 0),
          backgroundColor: PALETTE[1],
          borderRadius: 6,
          borderSkipped: false,
          stack: "a",
        },
        {
          label: "Rakennetekniikka",
          data: chartData.map(d => d["Rakennetekniikka"] || 0),
          backgroundColor: PALETTE[0],
          borderRadius: 6,
          borderSkipped: false,
          stack: "a",
        },
      ],
    },
    options: {
      animation: false,
      responsive: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: { font: { size: 13 }, boxWidth: 14, padding: 16 },
        },
      },
      scales: {
        x: {
          stacked: true,
          border: { display: false },
          grid: { display: false },
          ticks: { font: { size: 13 } },
        },
        y: {
          stacked: true,
          border: { display: false },
          grid: { color: "#eeeeee" },
          ticks: { font: { size: 13 } },
          max: yMax,
        },
      },
    },
    plugins: [stackTotalLabels],
  });

  const base64 = await canvasToBase64(canvas);
  chart.destroy();
  return base64;
}

const doughnutPercentageLabels = {
  id: "doughnutPercentageLabels",
  afterDatasetsDraw(chart) {
    const { ctx, data } = chart;
    const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
    if (!total) return;
    chart.getDatasetMeta(0).data.forEach((arc, i) => {
      const value = data.datasets[0].data[i];
      const pct = Math.round((value / total) * 100);
      if (pct < 4) return;
      const midAngle = arc.startAngle + (arc.endAngle - arc.startAngle) / 2;
      const r = (arc.innerRadius + arc.outerRadius) / 2;
      const x = arc.x + Math.cos(midAngle) * r;
      const y = arc.y + Math.sin(midAngle) * r;
      ctx.save();
      ctx.fillStyle = "#fff";
      ctx.font = "bold 30px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(`${pct}%`, x, y);
      ctx.restore();
    });
  },
};

const doughnutCenterText = {
  id: "doughnutCenterText",
  afterDatasetsDraw(chart) {
    const { ctx, data } = chart;
    const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
    if (!total) return;
    const meta = chart.getDatasetMeta(0);
    const { x, y } = meta.data[0];
    ctx.save();
    ctx.fillStyle = "#666666";
    ctx.font = "26px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Yhteensä", x, y - 28);
    ctx.fillStyle = "#222222";
    ctx.font = "bold 44px Arial";
    ctx.fillText(String(Math.round(total)), x, y + 28);
    ctx.restore();
  },
};

async function renderDoughnutChart(tekniikka, lvi, sahko, tutkimus) {
  const canvas = document.createElement("canvas");
  canvas.width = 840;
  canvas.height = 760;
  const ctx = canvas.getContext("2d");

  const chart = new Chart(ctx, {
    type: "doughnut",
    data: {
      labels: ["Rakennetekniikka", "LVI Järjestelmät", "Sähköjärjestelmät", "Lisätutkimukset"],
      datasets: [{
        data: [sum(tekniikka), sum(lvi), sum(sahko), sum(tutkimus)],
        backgroundColor: [PALETTE[0], PALETTE[1], PALETTE[2], PALETTE[3]],
        borderWidth: 0,
      }],
    },
    options: {
      animation: false,
      responsive: false,
      cutout: "60%",
      plugins: {
        legend: {
          position: "bottom",
          labels: { font: { size: 26 }, boxWidth: 28, padding: 24 },
        },
      },
    },
    plugins: [doughnutPercentageLabels, doughnutCenterText],
  });

  const base64 = await canvasToBase64(canvas);
  chart.destroy();
  return base64;
}

function buildYhteenvetoTable(ptsContext, showAlvZero = false, fontSize = 9, hide11thYear = false) {
  const { years: allYears, currentYear, tekniikkaYhteensa, lviYhteensa, sahkoYhteensa, tutkimusYhteensa, footerTotals } = ptsContext;
  // Display-only truncation — tekniikkaYhteensa/.../footerTotals stay the
  // full 11-length arrays and are only sliced at the point each is drawn.
  const years = hide11thYear ? allYears.slice(0, 10) : allYears;
  const small = fontSize * 0.9;

  const renderVal = (v) => (!v || v === 0 || v === "0") ? "" : String(v);

  const headerCell = (text, opts = {}) => ({
    text, bold: true, font: "Lato", fontSize: small,
    color: COLORS.headerText, fillColor: COLORS.green,
    alignment: "center", ...opts,
  });

  const rows = [];

  // Super-header
  rows.push([
    headerCell("Toimenpide-ehdotukset yhteensä", { colSpan: 2, alignment: "left" }), {},
    headerCell(showAlvZero
      ? `Kustannusarvio (x 1000€) Kustannustaso ${currentYear || new Date().getFullYear()} ALV 0%`
      : `Kustannusarvio (x 1000€) Kustannustaso ${currentYear || new Date().getFullYear()} sis. Alv 25,5%`,
      { colSpan: years.length, alignment: "right" }),
    ...Array(years.length - 1).fill({}),
  ]);

  // Year header
  rows.push([
    headerCell("Osa-alue", { alignment: "left" }),
    headerCell("Yhteensä"),
    ...years.map(y => headerCell(String(y))),
  ]);

  const dataRows = [
    { label: "Rakennetekniikka", values: tekniikkaYhteensa },
    { label: "LVI Järjestelmät",  values: lviYhteensa },
    { label: "Sähköjärjestelmät", values: sahkoYhteensa },
    { label: "Lisätutkimukset",   values: tutkimusYhteensa },
  ];

  dataRows.forEach(({ label, values }, idx) => {
    const bg = idx % 2 === 0 ? "#f2f2f2" : "#ffffff";
    const rowTotal = sum(values.slice(0, years.length));
    rows.push([
      { text: label, font: "Lato", fontSize: small, alignment: "left", fillColor: bg },
      { text: renderVal(rowTotal), font: "Lato", fontSize: small, alignment: "center", fillColor: bg },
      ...values.slice(0, years.length).map(v => ({ text: renderVal(v), font: "Lato", fontSize: small, alignment: "center", fillColor: bg })),
    ]);
  });

  // Totals footer
  const totalSum = sum((footerTotals || []).slice(0, years.length));
  rows.push([
    headerCell("YHTEENSÄ", { alignment: "left" }),
    headerCell(renderVal(totalSum)),
    ...(footerTotals || []).slice(0, years.length).map(v => headerCell(renderVal(v))),
  ]);

  return {
    table: {
      widths: [120, 40, ...Array(years.length).fill('*')],
      body: rows,
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft:   () => 3,
      paddingRight:  () => 3,
      paddingTop:    () => 3,
      paddingBottom: () => 3,
    },
  };
}

export async function renderYhteenvetoPtsBlock({ ptsContext, content, contentWidth = CONTENT_WIDTH, showAlvZero = false, hide11thYear = false }) {
  if (!ptsContext) return;

  const { chartData, years: allYears, tekniikkaYhteensa, lviYhteensa, sahkoYhteensa, tutkimusYhteensa } = ptsContext;
  // Display-only truncation — also slice chartData to match, since
  // Chart.js's labels/dataset arrays must stay the same length.
  const years = hide11thYear ? (allYears || []).slice(0, 10) : (allYears || []);
  const chartDataSliced = hide11thYear ? (chartData || []).slice(0, 10) : (chartData || []);

  const sliceVals = (arr) => (arr || []).slice(0, years.length);
  const [barBase64, doughnutBase64] = await Promise.all([
    renderBarChart(chartDataSliced, years),
    renderDoughnutChart(sliceVals(tekniikkaYhteensa), sliceVals(lviYhteensa), sliceVals(sahkoYhteensa), sliceVals(tutkimusYhteensa)),
  ]);

  // Bar chart — full content width
  content.push({
    image: barBase64,
    width: contentWidth,
    margin: [0, 0, 0, bottomMarginItemGlobal],
  });

  // Doughnut chart — centred, half content width
  content.push({
    image: doughnutBase64,
    width: Math.floor(contentWidth * 0.45),
    alignment: "center",
    margin: [0, 0, 0, bottomMarginItemGlobal],
  });

  // Summary table
  content.push({
    stack: [buildYhteenvetoTable(ptsContext, showAlvZero, 9, hide11thYear)],
    margin: [0, 0, 0, bottomMarginItemGlobal],
  });
}
